import {
  Box,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { DataGrid, GridPaginationModel } from "@mui/x-data-grid";
import NoMembersIcon from "~/images/settings/NoMembersIcon";
import { getSpaceAccessColumns } from "./SpaceAccessTableColumns";
import {
  usersDataGridSx,
  usersDataGridSlotProps,
  usersDataGridInitialState,
  getRowClassName,
} from "./UsersDataGridConfig";
import { SpaceAccess } from "../types";

export type SpaceAccessTableProps = {
  spaces: SpaceAccess[];
  searchValue: string;
  onSearchChange: (value: string) => void;
  onManageAccess: (space: SpaceAccess) => void;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  rowCount: number;
};

export function SpaceAccessTable({
  spaces,
  searchValue,
  onSearchChange,
  onManageAccess,
  paginationModel,
  onPaginationModelChange,
  rowCount,
}: SpaceAccessTableProps) {
  const columns = getSpaceAccessColumns({
    onManageAccess,
  });

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Space access and permissions
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Control the inventory this user can access, and their permission level
        </Typography>
      </Box>

      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search to filter spaces"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      {spaces.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 6,
            px: 2,
            textAlign: "center",
            bgcolor: "background.default",
            borderRadius: 1,
            minHeight: 200,
          }}
        >
          <NoMembersIcon sx={{ width: "48px", height: "48px", mb: 1 }} />
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "text.primary",
              mb: 1,
            }}
          >
            No space access
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
            }}
          >
            This user does not have access to any spaces.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <DataGrid
            rows={spaces}
            columns={columns}
            disableColumnMenu
            disableColumnResize
            disableRowSelectionOnClick
            getRowClassName={getRowClassName}
            initialState={usersDataGridInitialState}
            slotProps={usersDataGridSlotProps}
            sx={usersDataGridSx}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={onPaginationModelChange}
            rowCount={rowCount}
            pageSizeOptions={[5, 10, 25]}
          />
        </Box>
      )}
    </Paper>
  );
}
